import { UserOrganization } from '../api/models/apis';
import { TpActiveEnSelectionFilter } from './selection/interfaces';
import type { MutationLedger } from './mutation-ledger/schemas';

export type TpSelectedItemsList = Array<string>;

export type TpValueType = string; //'categorical' | 'number' | 'string';

export const VALTYPES = {
  CATEGORICAL: 'categorical',
  NUMBER: 'numerical',
  INTEGER: 'integer',
  STRING: 'string',
  TEXT: 'text',
  MULTICAT: 'multi_categorical',
  MULTISTRING: 'multival_string',
  DENDROGRAM: 'dendrogram',
  BOOLEAN: 'boolean',
};

export const VALTYPES_NON_NUMERIC = [
  VALTYPES.CATEGORICAL,
  VALTYPES.STRING,
  VALTYPES.TEXT,
  VALTYPES.MULTICAT,
  VALTYPES.MULTISTRING,
  VALTYPES.BOOLEAN,
];

export const MANIFEST_DATA_TYPE_TO_VALUE_TYPE = {
  [VALTYPES.MULTISTRING]: VALTYPES.MULTICAT,
};

export const OBJECTTYPES = {
  ENTITY_TABLE: 'entity_table',
  BITMAP: 'bitmap',
  DENDROGRAM: 'entity_dendrogram',
  LAZY_LOADING_TABLE: 'lazy_loading_table',
};

export const VALTYPE_ANY = 'any';

export const UNBOUND_COLUMN = '_none_';

export const SORTDIR = {
  ASC: 'asc',
  DESC: 'desc',
};

export const valtypes_sortable = [
  VALTYPES.CATEGORICAL,
  VALTYPES.NUMBER,
  VALTYPES.INTEGER,
  VALTYPES.STRING,
  VALTYPES.MULTICAT,
];

export function isMultiValuedType(valType: string) {
  return valType == VALTYPES.MULTISTRING || valType == VALTYPES.MULTICAT;
}

export function isEncoded(valType: string) {
  return valType == VALTYPES.CATEGORICAL || valType == VALTYPES.MULTICAT;
}

export interface TpDataTableColumn {
  id: string;
  name: string;
  description?: string | null;
  groupId?: string | null;
  valueType: TpValueType;
  columnCanonicalTypeId?: string; // if present, it means that columns thenselves refer to entities, and here we define their type; typically used to define a matrix that contains cross data for two types of entities (rows & columns)
  columnItemId?: string; // in conjuction with columnCanonicalTypeId: represent the identifier of the item the column refers to
  decimalDigits?: number; //for 'number' valuetypes, specifies the number of decimal digits
  catConvertor?: string[]; // for categorical valuetypes, convert the integer coding of a category to the category string name
  catConvertorInv?: { [catName: string]: number }; // for categorical valuetypes, convert the category string name to the integer coding of a category
  augmentedData: {
    categories?: { value: string; count: number }[]; // ToDo: fix this type as the value is currently a number instead of a string.
    cat2Index?: { [value: string]: number }; // maps categories to index numbers, sorte by category frequency = useful for color coding
  };
}

export interface TpDataTableGroup {
  id: string;
  name: string;
  description?: string | null;
}

export interface TpDataTableMeta {
  columns: TpDataTableColumn[];
  columnGroups: TpDataTableGroup[];
  id: string; // identifier of this datatable
  name: string;
  description?: string | null;
  canonicalTypeId: string; // identifier of the type of the data held in table rows (=items)
  colItemId: string; // identifier of the column holding unique ids per row (=items)
  colLabel: string; // identifier of the column holding the display label. If absent, the identifier is used
  defaultHoverLabel?: string; // column id of the column used by default to show hover labels

  relationsForEntity?: string; // if defined, this table contains relations for another entity type, mentioned here
  relationFrom?: string; // in case this table contains relations, column ID of the "from" entity (should match primary key of the other entity)
  relationTo?: string; // in case this table contains relations, column ID of the "to" entity (should match primary key of the other entity)
}

export interface TpDataTable extends TpDataTableMeta {
  columnData: { [colId: string]: any }; // data per column
  itemIdMap: { [id: string]: number }; //lookup table, linking row identifiers to row indexes
  baseColumnIds?: string[];
  baseColumnGroupIds?: string[];
}

// Describes one derived (non-base) column to be created or updated on a data table, computed by an
// action step's pure compute logic. Applied (possibly several at once) via `upsertDerivedColumns`.
export interface TpDerivedColumnPatch {
  tableId: string;
  columnId: string;
  columnName: string;
  columnDescription: string | null;
  valueType: TpValueType;
  decimalDigits?: number | null;
  groupId: string | null;
  groupName: string | null;
  groupDescription: string | null;
  columnData: any[];
}

export interface TpDendrogramLink {
  idIn1: string; // in case an actual item is linked, it has the item id; if a cluster is linked, it has the cluster output id
  idIn2: string; // see above
  idOut: string;
  distance: number;
}

export interface TpDendrogram {
  id: string;
  name: string;
  description: string;
  canonicalTypeId: string;
  links: TpDendrogramLink[]; // links to be executed in order of appearance in this list
  itemId2PosIndexMap: { [itemId: string]: number }; // Maps item ids to positions in the dendrogram
  tree: any;
}

export interface TpBitmapCoordinates {
  // (x, y) is the top left corner of the image
  x: number;
  y: number;
  width: number;
  height: number;
}

export interface TpBitmap {
  id: string;
  name: string;
  description: string;
  canonicalTypeId: string; // Mostly here for backwards compatibility.
  fileLocation: string;
  content: string; // base64 content used for downloading and adding to HTMLImageElement
  imageData: HTMLImageElement | ImageBitmap | null; // Can be added to canvas
  isPlotOverlay: boolean;
  rectangle: TpBitmapCoordinates | null;
  tags: string[];
}

export interface TpCanonicalTypeInfo {
  // info about a certain kind of data items ("canonical type")
  id: string;
  name: string;
  itemIdList: string[]; // the list of all item IDs in the full set of items
  selectedItemsMap: { [itemId: string]: boolean } | null;
  selectionFilter: TpActiveEnSelectionFilter;

  restrictionFilter: TpActiveEnSelectionFilter | null;
  restrictedItemsMap: { [itemId: string]: boolean } | null; // returns true/false to determine membership of an item in the current restriction list. if null, no restriction is applied
  restrictionList: number[] | null; // list of indexes in the full item set of those items that are part of the restricted set
  restrictedItemId2Inxdex: { [itemId: string]: number }; // maps an item identifier to the index in the currently used list (restricted or full)
}

export interface TpDataContext {
  // an ecosystem of data sets that are related to each other and can be jointly analysed
  assetId: string; // identifier of the asset this data context belongs to
  id: string;
  name: string;
  dataTables: TpDataTable[];
  lazyLoadingTableMetas: TpDataTableMeta[];
  dendrograms: TpDendrogram[];
  bitmaps: { [id: string]: TpBitmap };
  canonicalTypes: { [canonicalTypeId: string]: TpCanonicalTypeInfo };
  mutationLedger: MutationLedger;
}

export interface TpPostFetchCallback {
  openEntity: boolean;
  callback: () => void;
  canonicalTypeId?: string;
  itemId?: string;
}

export interface TpAnalyticsData {
  // totality of all data context. Note that this information is not kept in the store for performance reasons
  dataContexts: { [id: string]: TpDataContext };
  postFetchCallBacks: { [id: string]: TpPostFetchCallback };
}

export interface TpDataContextRef {
  // data structure kept in store, referring to a single data context
  id: string;
  statusIndex: number; // this is incremented to notify the store & dependencies that something was changed on the data context
}

export interface TpAnalyticsDataRefs {
  // totality of all referencs to data contexts, to be keot in the store
  dataContextRefs: { [id: string]: TpDataContextRef };
}

export type TpUserInfo = {
  email: string | undefined;
  name?: string | undefined;
  avatar?: string | undefined;
  canWrite: boolean;
  isOrganizationAdmin: boolean;
  acceptedEulaVersion?: string | undefined;
  organizations: UserOrganization[];
  isSystemAdmin?: boolean;
};
